import { useEffect, useRef, useState } from 'react';
import { ArrowLeft, CheckCircle2, LogOut, Shield, UserRound, SlidersHorizontal } from 'lucide-react';
import { AppearanceSettings, applyAppearance, defaultAppearance, type Appearance } from './AppearanceSettings';
import { AccountSettings } from './AccountSecurity';
import { ProfileForm, type AccountState, type User } from './AccountJourney';
import { api, setProfileId } from './api';
import { ErrorNotice, Spinner } from './components';
import { accountWebBuild, desktopAccountRequest } from './desktopAccountBridge';
import './account.css';
import './desktop-account.css';

export function DesktopAccount() {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true),
    [busy, setBusy] = useState(false);
  const [tab, setTab] = useState<'profile' | 'security' | 'appearance'>('profile');
  const [appearance, setAppearance] = useState<Appearance>(defaultAppearance);
  useEffect(() => {
    const changed = (event: Event) => {
      const value = (event as CustomEvent<{ appearance: Appearance; dark: boolean }>).detail;
      if (!value?.appearance || typeof value.dark !== 'boolean') return;
      setAppearance(value.appearance);
      applyAppearance(value.appearance, value.dark);
    };
    window.addEventListener('marvin-host-appearance', changed);
    return () => window.removeEventListener('marvin-host-appearance', changed);
  }, []);
  const [supportsAppearance, setSupportsAppearance] = useState(false);
  const [supportsProfileLanguage, setSupportsProfileLanguage] = useState(false);
  const [supportsCitationPreference, setSupportsCitationPreference] = useState(false);
  const [supportsProfileState, setSupportsProfileState] = useState(false);
  const [supportsBrowserPasskeys, setSupportsBrowserPasskeys] = useState(false);
  const [supportsDeviceCleanup, setSupportsDeviceCleanup] = useState(false);
  const [supportsProfileVoice, setSupportsProfileVoice] = useState(false);
  const [profileDirty, setProfileDirty] = useState(false);
  const [error, setError] = useState(''),
    [notice, setNotice] = useState('');
  const [ended, setEnded] = useState(false),
    [profileVersion, setProfileVersion] = useState(0);
  const panelTitle = useRef<HTMLHeadingElement>(null);
  const panel = useRef<HTMLDivElement>(null);
  const scrollPositions = useRef({profile: 0, security: 0, appearance: 0});
  const load = () => {
    setLoading(true);
    setError('');
    desktopAccountRequest<AccountState & { capabilities?: string[]; appearance?: Appearance; dark?: boolean; initialSection?: string }>('hello')
      .then((state) => {
        setUser(state.user);
        if (state.user) setProfileId(state.user.id, state.user.email);
        setSupportsAppearance(state.capabilities?.includes('appearance') ?? false);
        setSupportsProfileLanguage(state.capabilities?.includes('profileLanguage') ?? false);
        setSupportsCitationPreference(state.capabilities?.includes('citationPreference') ?? false);
        setSupportsProfileState(state.capabilities?.includes('profileState') ?? false);
        setSupportsBrowserPasskeys(state.capabilities?.includes('nativePasskeys') ?? false);
        setSupportsDeviceCleanup(state.capabilities?.includes('deviceCleanup') ?? false);
        setSupportsProfileVoice(state.capabilities?.includes('profileVoice') ?? false);
        if (state.appearance) { setAppearance(state.appearance); applyAppearance(state.appearance, state.dark); }
        else applyAppearance({ ...defaultAppearance, theme: 'light' });
        if (state.initialSection === 'appearance') setTab('appearance');
        if (state.initialSection === 'security') setTab('security');
        if (!state.user) {
          setEnded(true);
          setNotice('Your session has ended. Return to sign in.');
        } else if (state.user.pendingEmail || !state.user.emailVerified) setTab('security');
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  };
  const started = useRef(false);
  const lastProfileState = useRef<boolean | null>(null);
  const profileCloseBlocked = supportsProfileState && profileDirty;
  const profileCloseReason = 'Save or cancel your profile changes before leaving.';
  useEffect(() => {
    if (!supportsProfileState || lastProfileState.current === profileDirty) return;
    lastProfileState.current = profileDirty;
    void desktopAccountRequest('profileState', { dirty: profileDirty ? 'true' : 'false' }).catch(e => setError(e.message));
  }, [profileDirty, supportsProfileState]);
  useEffect(() => {
    const close = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !busy) {
        event.preventDefault();
        if (profileCloseBlocked) { setError(profileCloseReason); return; }
        void desktopAccountRequest('close').catch(e => setError(e.message));
      }
    };
    window.addEventListener('keydown', close);
    return () => window.removeEventListener('keydown', close);
  }, [busy, profileCloseBlocked]);
  useEffect(() => {
    if (!started.current) {
      started.current = true;
      load();
    }
  }, []);
  function selectTab(value: typeof tab) {
    if (busy || value === tab) return;
    scrollPositions.current[tab] = panel.current?.scrollTop ?? 0;
    setTab(value);
    setNotice('');
    requestAnimationFrame(() => {
      if (panel.current) panel.current.scrollTop = scrollPositions.current[value];
      panelTitle.current?.focus({ preventScroll: true });
    });
  }
  async function signOut() {
    if (busy || profileCloseBlocked) return;
    setError('');
    setBusy(true);
    try {
      await api('/account/logout', 'POST', {});
      setEnded(true);
      setNotice('You have signed out.');
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <main className="account-shell desktop-account modern-account" data-web-build={accountWebBuild}>
      {loading ? (
        <Spinner label="Opening your account…" />
      ) : ended ? (
        <p role="status">{notice}</p>
      ) : user ? (
        <div className="account-settings-layout">
          <aside className="account-sidebar">
            <div className="account-identity">
              <span className="account-avatar" aria-hidden="true">
                {user.initials || user.name.slice(0, 1)}
              </span>
              <div>
                <strong>{user.name}</strong>
                <span title={user.email}>{user.email}</span>
              </div>
            </div>
            <nav className="account-section-nav" aria-label="Account settings">
              <button
                id="account-nav-profile"
                aria-current={tab === 'profile' ? 'page' : undefined}
                onClick={() => selectTab('profile')}
                disabled={busy}
              >
                <UserRound size={18} aria-hidden="true" />
                Profile
              </button>
              <button
                id="account-nav-security"
                aria-current={tab === 'security' ? 'page' : undefined}
                onClick={() => selectTab('security')}
                disabled={busy}
              >
                <Shield size={18} aria-hidden="true" />
                Security
              </button>
              <div className="account-nav-divider" />
              {supportsAppearance && <button id="account-nav-appearance" aria-current={tab === 'appearance' ? 'page' : undefined}
                onClick={() => selectTab('appearance')} disabled={busy}><SlidersHorizontal size={16} aria-hidden="true" />Appearance</button>}
              <button onClick={() => void signOut()} disabled={busy || profileCloseBlocked} title={profileCloseBlocked ? profileCloseReason : undefined}>
                <LogOut size={18} aria-hidden="true" />
                Sign out
              </button>
            </nav>
            <button
              className="account-return"
              disabled={busy || profileCloseBlocked}
              title={profileCloseBlocked ? profileCloseReason : undefined}
              onClick={() => { if (!busy && !profileCloseBlocked) void desktopAccountRequest('close').catch((e) => setError(e.message)); }}
            >
              <ArrowLeft size={16} aria-hidden="true" />
              Back to my workspace
            </button>
            {profileCloseBlocked && <p className="account-hint">{profileCloseReason}</p>}
          </aside>
          <div ref={panel} className="account-main-panel">
            <header className="account-panel-heading">
              <h1 ref={panelTitle} tabIndex={-1}>
                {tab === 'profile' ? 'Your profile' : tab === 'security' ? 'Security' : 'Appearance'}
              </h1>
              <p>
                {tab === 'profile'
                  ? 'Details about you and your studies to personalise your workspace.'
                  : tab === 'security' ? 'Manage your sign-in details and account access.' : 'Make room for your work.'}
              </p>
            </header>
            {/* Keep forms mounted: switching sections must not discard unsaved edits. */}
            <div className="account-view" hidden={tab !== 'profile'}>
              <ProfileForm
                key={profileVersion}
                user={user}
                embedded
                nativeVoice
                nativeVoiceAvailable={supportsProfileVoice}
                onBusyChange={setBusy}
                responseLocaleAvailable={supportsProfileLanguage}
                citationPreferenceAvailable={supportsCitationPreference}
                onDirtyChange={setProfileDirty}
                onEdit={() => setNotice('')}
                onCancel={() => {
                  setProfileVersion((v) => v + 1);
                  setNotice('');
                }}
                onSaved={(value) => {
                  setUser(value);
                  setNotice('Your profile has been saved.');
                }}
              />
              {notice && (
                <p role="status" className="account-save-notice">
                  <CheckCircle2 size={18} aria-hidden="true" />
                  {notice}
                </p>
              )}
            </div>
            <div className="account-view" hidden={tab !== 'security'}>
              <AccountSettings
                user={user}
                desktop
                onOpenPasskeys={supportsBrowserPasskeys ? async () => { await desktopAccountRequest('openPasskeys'); } : undefined}
                onRemoveDevice={supportsDeviceCleanup ? async () => { await desktopAccountRequest('openDeviceCleanup'); } : undefined}
                deviceRemovalBlocked={profileCloseBlocked}
                onBusyChange={setBusy}
                onChanged={setUser}
                onVerified={(state) => {
                  if (state.user) setUser(state.user);
                }}
                onEnded={(message) => {
                  setEnded(true);
                  setNotice(message);
                }}
              />
            </div>
            {supportsAppearance && <div className="account-view" hidden={tab !== 'appearance'}><AppearanceSettings initial={appearance} hosted /></div>}
          </div>
        </div>
      ) : null}
      {error && (
        <>
          <ErrorNotice message={error} />
          {!user && (
            <button className="button secondary" onClick={load}>
              Retry account connection
            </button>
          )}
        </>
      )}
    </main>
  );
}
